import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { API_BASE_URL } from '../utils/apiConfig';
import { supabase } from '../utils/supabase';
import NumberInput from './ui/NumberInput';

interface DeleteMembershipDialogProps {
    isOpen: boolean;
    membership: {
        id: string;
        plan: {
            name: string;
        };
        startDate: string;
        endDate: string;
        baseAmount?: number;
        taxAmount?: number;
        taxPercent?: number;
        totalAmount?: number;
        discountAmount?: number;
        discountPercent?: number;
    };
    memberId: string;
    onClose: () => void;
    onSuccess: () => void;
}

const CANCELLATION_REASONS = [
    'Medical',
    'Personal',
    'Other'
];

export default function DeleteMembershipDialog({
    isOpen,
    membership,
    memberId,
    onClose,
    onSuccess
}: DeleteMembershipDialogProps) {
    const [reason, setReason] = useState('');
    const [notes, setNotes] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [issueRefund, setIssueRefund] = useState(false);
    const [refundAmount, setRefundAmount] = useState<number>(0);

    // Update refund amount when totalAmount changes or dialog opens
    useEffect(() => {
        if (membership.totalAmount) {
            setRefundAmount(membership.totalAmount);
        }
    }, [membership.totalAmount, isOpen]);

    if (!isOpen) return null;

    const handleSubmit = async () => {
        if (!reason) {
            toast.error('Please select a cancellation reason');
            return;
        }

        if (issueRefund) {
            if (!notes) {
                toast.error('Additional notes are mandatory when issuing a refund');
                return;
            }
            if (refundAmount < 0) {
                toast.error('Refund amount cannot be negative');
                return;
            }
            if (refundAmount > (membership.totalAmount || 0)) {
                toast.error(`Refund amount cannot exceed total paid (₹${membership.totalAmount})`);
                return;
            }
        }

        setIsSubmitting(true);
        try {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session?.access_token) {
                throw new Error('Not authenticated');
            }

            const response = await fetch(
                `${API_BASE_URL}/api/members/${memberId}/memberships/${membership.id}`,
                {
                    method: 'DELETE',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${session.access_token}`
                    },
                    body: JSON.stringify({
                        reason,
                        notes,
                        issueRefund,
                        refundAmount: issueRefund ? refundAmount : 0
                    })
                }
            );

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || 'Failed to cancel membership');
            }

            toast.success(issueRefund ? 'Membership refunded successfully' : 'Membership cancelled successfully');
            onSuccess();
            handleClose();
        } catch (error: any) {
            console.error('Error cancelling membership:', error);
            toast.error(error.message || 'Failed to cancel membership');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        setReason('');
        setNotes('');
        setIssueRefund(false);
        onClose();
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-bold text-gray-900">Cancel Membership</h2>
                    <button
                        onClick={handleClose}
                        disabled={isSubmitting}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Membership Info */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                    <div className="flex items-start gap-3">
                        <svg className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        <div className="flex-1">
                            <p className="text-sm font-medium text-yellow-900 mb-1">
                                You are about to cancel this membership:
                            </p>
                            <p className="text-sm text-yellow-800">
                                <span className="font-semibold">{membership.plan.name}</span>
                            </p>
                            <p className="text-xs text-yellow-700 mt-1">
                                {new Date(membership.startDate).toLocaleDateString()} - {new Date(membership.endDate).toLocaleDateString()}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Reason Selection */}
                <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Cancellation Reason <span className="text-red-500">*</span>
                    </label>
                    <select
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        disabled={isSubmitting}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                        required
                    >
                        <option value="">Select a reason</option>
                        {CANCELLATION_REASONS.map((r) => (
                            <option key={r} value={r}>
                                {r}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Issue Refund Checkbox */}
                <div className="mb-4 flex items-center gap-2">
                    <input
                        type="checkbox"
                        id="issueRefund"
                        checked={issueRefund}
                        onChange={(e) => setIssueRefund(e.target.checked)}
                        className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500 border-gray-300"
                    />
                    <label htmlFor="issueRefund" className="text-sm font-medium text-gray-700">
                        Issue Refund
                    </label>
                </div>

                {/* Refund Details */}
                {issueRefund && (
                    <div className="space-y-4 mb-4">
                        <div className="bg-gray-50 rounded-lg p-4 text-sm space-y-2">
                            <h3 className="font-bold text-gray-900 border-b pb-2 mb-2">Payment Details</h3>
                            <div className="flex justify-between">
                                <span className="text-gray-600">Plan Price:</span>
                                <span>₹{Number(membership.baseAmount || 0).toLocaleString()}</span>
                            </div>
                            {membership.discountAmount && membership.discountAmount > 0 && (
                                <div className="flex justify-between text-green-600">
                                    <span>Discount ({membership.discountPercent}%):</span>
                                    <span>-₹{Number(membership.discountAmount).toLocaleString()}</span>
                                </div>
                            )}
                            <div className="flex justify-between">
                                <span className="text-gray-600">GST ({membership.taxPercent}%):</span>
                                <span>₹{Number(membership.taxAmount || 0).toLocaleString()}</span>
                            </div>
                            <div className="flex justify-between font-bold text-gray-900 pt-2 border-t mt-2">
                                <span>Total Paid:</span>
                                <span>₹{Number(membership.totalAmount || 0).toLocaleString()}</span>
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Refund Amount <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-bold">₹</span>
                                <NumberInput
                                    min={0}
                                    emptyValue={0}
                                    value={refundAmount}
                                    onChange={(v) => setRefundAmount(v ?? 0)}
                                    placeholder="0.00"
                                    className="w-full pl-8 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>
                            <p className="text-xs text-gray-500 mt-1 italic">Defaults to total payment made</p>
                        </div>
                    </div>
                )}

                {/* Notes */}
                <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Additional Notes {issueRefund ? <span className="text-red-500">*</span> : '(Optional)'}
                    </label>
                    <textarea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        disabled={isSubmitting}
                        placeholder={issueRefund ? "Please provide details for the refund..." : "Enter any additional details..."}
                        rows={3}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none disabled:bg-gray-100 disabled:cursor-not-allowed"
                        required={issueRefund}
                    />
                </div>

                {/* Actions */}
                <div className="flex gap-3">
                    <button
                        onClick={handleClose}
                        disabled={isSubmitting}
                        className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Keep Membership
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={isSubmitting || !reason || (issueRefund && !notes)}
                        className={`flex-1 px-4 py-2.5 ${issueRefund ? 'bg-blue-600 hover:bg-blue-700' : 'bg-red-600 hover:bg-red-700'} text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2`}
                    >
                        {isSubmitting ? (
                            <>
                                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                {issueRefund ? 'Refunding...' : 'Cancelling...'}
                            </>
                        ) : (
                            issueRefund ? 'Process Refund' : 'Cancel Membership'
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
}
